import { s, h } from '../lib/sx'
import { A } from '../lib/router'
import { Plate } from '../components/Plate'
import { projects, type Project } from '../content/projects'

/** Building-work sectors are graded GB; everything else is graded CE. */
const GB_SECTORS = new Set(['Real Estate', 'Industrial'])

export function ProjectDetail({ project }: { project: Project }) {
  const meta: [string, string][] = [
    ['Client', project.client],
    ['Sector', project.sector],
    ['Location', project.province],
    ['Contract value', project.value],
    ['Programme', project.dur],
    ['Period', project.year],
    ['Our role', project.role],
    ['Status', project.status],
  ]

  const titleBlock: [string, string][] = [
    ['Sheet ref', `SLX-${String(projects.indexOf(project) + 1).padStart(3, '0')}`],
    ['Grading', GB_SECTORS.has(project.sector) ? '6GB PE' : '6CE PE'],
    ['Revision', 'A · Aug 2026'],
    ['Status', project.status],
  ]

  const related = projects.filter((p) => p.sector === project.sector && p.slug !== project.slug).slice(0, 3)

  return (
    <div>
      <section style={s('background:#1B2430;color:#F2F4F6;')}>
        <div style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,56px) clamp(18px,4vw,32px) clamp(48px,7vw,72px);')}>
          <A href="/projects" style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:#8C97A4;")} className={h('color:#FFFFFF;')}>
            ← All projects
          </A>
          <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#E06A20;margin-top:34px;")}>
            {project.sector} · {project.province}
          </div>
          <h1 style={s('font-family:Archivo,sans-serif;font-weight:800;font-size:clamp(38px,4.8vw,68px);line-height:0.96;letter-spacing:-0.038em;margin:20px 0 0;max-width:1000px;')}>{project.title}</h1>
        </div>
      </section>

      <Plate
        box="aspect-ratio:21/7;display:flex;align-items:flex-end;padding:24px clamp(18px,4vw,32px);"
        src={project.image}
        eager
        caption={project.photo}
        captionStyle="padding:8px 12px;font-size:11px;border-left:2px solid #1F5C99;"
      />

      <section style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,80px) clamp(18px,4vw,32px);display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:clamp(40px,5vw,80px);align-items:start;')}>
        <div>
          <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#7E8894;margin-bottom:16px;")}>Scope of works</div>
          <p style={s('font-size:19px;line-height:1.6;margin:0 0 40px;text-wrap:pretty;')}>{project.scope}</p>
          <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,240px),1fr));gap:1px;background:#DCE0E5;border:1px solid #DCE0E5;')}>
            {project.facts.map(([label, value], i) => (
              <div key={i} style={s('background:#F2F4F6;padding:26px 24px;')}>
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#7E8894;")}>{label}</div>
                <div style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:24px;letter-spacing:-0.02em;margin-top:10px;')}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={s('border-top:2px solid #1B2430;')}>
          {meta.map(([k, v]) => (
            <div key={k} style={s('display:flex;justify-content:space-between;gap:20px;padding:16px 0;border-bottom:1px solid #DCE0E5;')}>
              <span style={s("font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#7E8894;")}>{k}</span>
              <span style={s('font-size:15px;font-weight:500;text-align:right;')}>{v}</span>
            </div>
          ))}
          <A href="/quote" style={s('display:block;text-align:center;background:#1F5C99;color:#FFFFFF;padding:16px;font-size:14px;font-weight:600;margin-top:28px;')} className={h('background:#17497A;color:#FFFFFF;')}>
            Discuss a similar project
          </A>
        </div>
      </section>

      <section style={s('max-width:1320px;margin:0 auto;padding:0 clamp(18px,4vw,32px) 90px;')}>
        <div style={s('border:1px solid #1B2430;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,160px),1fr));')}>
          <div style={s('background:#1B2430;color:#F2F4F6;padding:26px 28px;display:flex;flex-direction:column;justify-content:space-between;gap:22px;')}>
            <span style={s('font-family:Archivo,sans-serif;font-weight:800;font-size:19px;letter-spacing:0.1em;')}>SAMLORYX</span>
            <span style={s("font-family:'IBM Plex Mono',monospace;font-size:9.5px;letter-spacing:0.22em;text-transform:uppercase;color:#8C97A4;")}>Contract record</span>
          </div>
          {titleBlock.map(([k, v]) => (
            <div key={k} style={s('padding:26px 22px;border-left:1px solid #C3C9D1;')}>
              <div style={s("font-family:'IBM Plex Mono',monospace;font-size:9.5px;letter-spacing:0.18em;text-transform:uppercase;color:#7E8894;")}>{k}</div>
              <div style={s("font-family:'IBM Plex Mono',monospace;font-size:14px;color:#1B2430;margin-top:12px;")}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section style={s('background:#DCE0E5;')}>
          <div style={s('max-width:1320px;margin:0 auto;padding:clamp(48px,7vw,80px) clamp(18px,4vw,32px);')}>
            <h2 style={s('font-family:Archivo,sans-serif;font-weight:700;font-size:clamp(28px,3vw,40px);letter-spacing:-0.03em;margin:0 0 36px;')}>Related work</h2>
            <div style={s('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,240px),1fr));gap:28px;')}>
              {related.map((p) => (
                <A key={p.slug} href={`/projects/${p.slug}`} style={s('display:block;background:#F2F4F6;padding:28px 26px;')} className={h('background:#1B2430;color:#F2F4F6;')}>
                  <div style={s("font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.16em;text-transform:uppercase;color:#A8500F;")}>{p.province}</div>
                  <h3 style={s('font-family:Archivo,sans-serif;font-weight:600;font-size:19px;line-height:1.25;margin:14px 0 10px;')}>{p.title}</h3>
                  <div style={s("font-family:'IBM Plex Mono',monospace;font-size:11.5px;color:#7E8894;")}>
                    {p.value} · {p.status}
                  </div>
                </A>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
